interface VersionInfo {
  version: string;
  buildDate: Date;
  lastUpdate: Date;
  changeCount: number;
}

class VersionManager {
  private static readonly VERSION_KEY = 'crm_version_info';
  private static readonly BASE_VERSION = '1.0.0';

  // Get current version info
  static getVersionInfo(): VersionInfo {
    try {
      const stored = localStorage.getItem(this.VERSION_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return {
          ...parsed,
          buildDate: new Date(parsed.buildDate),
          lastUpdate: new Date(parsed.lastUpdate)
        };
      }
    } catch (error) {
      console.error('Error loading version info:', error);
    }

    // Default version info
    const defaultVersion: VersionInfo = {
      version: this.BASE_VERSION,
      buildDate: new Date(),
      lastUpdate: new Date(),
      changeCount: 0
    };

    this.saveVersionInfo(defaultVersion);
    return defaultVersion;
  }

  // Save version info
  private static saveVersionInfo(versionInfo: VersionInfo): void {
    try {
      localStorage.setItem(this.VERSION_KEY, JSON.stringify(versionInfo));
    } catch (error) {
      console.error('Error saving version info:', error);
    }
  }

  // Increment version based on change type
  static incrementVersion(changeType: 'major' | 'minor' | 'patch' = 'patch'): VersionInfo {
    const currentInfo = this.getVersionInfo();
    const versionParts = currentInfo.version.split('.').map(Number);
    
    switch (changeType) {
      case 'major':
        versionParts[0]++;
        versionParts[1] = 0;
        versionParts[2] = 0;
        break;
      case 'minor':
        versionParts[1]++;
        versionParts[2] = 0;
        break;
      case 'patch':
      default:
        versionParts[2]++;
        break;
    }

    const newVersion: VersionInfo = {
      version: versionParts.join('.'),
      buildDate: currentInfo.buildDate,
      lastUpdate: new Date(),
      changeCount: currentInfo.changeCount + 1
    };

    this.saveVersionInfo(newVersion);
    console.log(`🔄 Versión actualizada: ${currentInfo.version} → ${newVersion.version}`);
    return newVersion;
  }

  // Track user actions that should increment version
  static trackUserAction(action: string): void {
    const actions = [
      'create_contact', 'update_contact', 'delete_contact',
      'create_company', 'update_company', 'delete_company',
      'create_meeting', 'update_meeting', 'delete_meeting',
      'create_activity', 'update_activity', 'delete_activity',
      'create_form', 'update_form', 'delete_form',
      'create_user', 'update_user', 'delete_user',
      'create_collaborator', 'update_collaborator', 'delete_collaborator',
      'create_project', 'update_project', 'delete_project',
      'create_timesheet', 'approve_timesheet', 'reject_timesheet',
      'create_invoice', 'validate_invoice', 'create_payment',
      'update_settings', 'update_email_template'
    ];

    if (actions.includes(action)) {
      // Increment patch version for any significant change
      this.incrementVersion('patch');
    }
  }

  // Get formatted version string
  static getFormattedVersion(): string {
    const info = this.getVersionInfo();
    return `v${info.version}`;
  }

  // Get build info string
  static getBuildInfo(): string {
    const info = this.getVersionInfo();
    const daysSinceBuild = Math.floor(
      (new Date().getTime() - info.buildDate.getTime()) / (1000 * 60 * 60 * 24)
    );
    
    return `Build ${info.changeCount} (${daysSinceBuild} días)`;
  }

  // Get last update info
  static getLastUpdateInfo(): string {
    const info = this.getVersionInfo();
    const now = new Date();
    const diff = now.getTime() - info.lastUpdate.getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 1) return 'Actualizado ahora';
    if (minutes < 60) return `Actualizado hace ${minutes}m`;
    if (hours < 24) return `Actualizado hace ${hours}h`;
    return `Actualizado hace ${days}d`;
  }

  // Reset version (for development/testing)
  static resetVersion(): VersionInfo {
    const resetVersion: VersionInfo = {
      version: this.BASE_VERSION,
      buildDate: new Date(),
      lastUpdate: new Date(),
      changeCount: 0
    };

    this.saveVersionInfo(resetVersion);
    return resetVersion;
  }

  // Get current version string (for session integrity)
  static getCurrentVersion(): string {
    const info = this.getVersionInfo();
    return info.version;
  }

  // Get previous version from storage (for deployment detection)
  static getPreviousVersion(): string | null {
    try {
      const stored = localStorage.getItem('crm_previous_version');
      return stored;
    } catch (error) {
      return null;
    }
  }

  // Store current version as previous (call on app init)
  static storePreviousVersion(): void {
    try {
      const current = this.getCurrentVersion();
      localStorage.setItem('crm_previous_version', current);
    } catch (error) {
      console.error('Error storing previous version:', error);
    }
  }
}

// Export both default and named
export const versionManager = VersionManager;
export default VersionManager;